import React, { useContext, useEffect, useState } from 'react';
import { ActivityIndicatorContext } from '../../providers/ActivityIndicatorProvider/context';
import useFormTextState from '../../hooks/useFormTextState';
import useSearchDropdown, { getSearchDropdownItems, SearchDropdownItem } from '../../hooks/useSearchDropdown';
import { getDisplayNames } from '../../api/utils';
import { filteringModel, tableColumns } from '../../components/ProjectTable/constants';
import ProjectContainer from '../../components/containers/ProjectContainer';
import { Components } from './styled';
import ProjectSearchDropdown from '../../components/ProjectSearchDropdown';
import ProjectTableHeader from '../../components/ProjectTableHeader';
import ProjectTable from '../../components/ProjectTable';
import { FORM_QUESTION_TYPES, FormQuestion, FormQuestionType } from '../../api/types/forms';
import { getAllFormQuestions } from '../../api/forms';
import CreateOrEditFormQuestionModal from '../../components/modals/CreateOrEditFormQuestionModal';
import useModalState from '../../hooks/useModalState';
import ProjectPicker from '../../components/ProjectPicker';

const Forms = () => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);

  const [data, setData] = useState<FormQuestion[]>([]);

  const [formQuestionType, setFormQuestionType] = useState<FormQuestionType>();

  const searchProps = useFormTextState();

  const createFormQuestionModalProps = useModalState<{
    formQuestion?: FormQuestion;
    questionType?: FormQuestionType;
  }>();

  const formQuestionTypePicker = useSearchDropdown({
    items: getSearchDropdownItems<string>(FORM_QUESTION_TYPES, (item) => getDisplayNames.formQuestionType(item) || ''),
    dropdownType: 'basic',
    singleSelect: true,
  });
  const refreshData = async () => {
    setIsLoading(true);
    const result = await getAllFormQuestions({
      formQuestionType: formQuestionTypePicker.selectedItems.map((item) => item.value)?.[0],
    });
    setIsLoading(false);
    if (result && Array.isArray(result)) {
      setData(result);
    }
  };

  useEffect(() => {
    const defaultFormQuestionType = 'reflection';
    formQuestionTypePicker.setSelectedItems(() => {
      return [
        {
          value: defaultFormQuestionType,
          label: getDisplayNames.formQuestionType(defaultFormQuestionType),
          searchLabel: defaultFormQuestionType,
        },
      ] as SearchDropdownItem[];
    });
  }, []);

  useEffect(() => {
    if (!!formQuestionType && formQuestionType?.length > 0) {
      refreshData().then();
    }
  }, [formQuestionType]);

  useEffect(() => {
    setFormQuestionType(formQuestionTypePicker.selectedItems.map((item) => item.value)?.[0]);
  }, [formQuestionTypePicker.selectedItems]);

  const onEditFormQuestion = (id: string) => {
    const foundFormQuestion = data?.find((item) => item?.id === id);
    if (foundFormQuestion) {
      createFormQuestionModalProps.open({ formQuestion: foundFormQuestion, questionType: formQuestionType });
    }
  };

  const onAddFormQuestion = () => {
    createFormQuestionModalProps.open({ formQuestion: undefined, questionType: formQuestionType });
  };

  const onCreateOrEditModalClose = (refreshTrigger?: boolean) => {
    if (refreshTrigger) {
      refreshData?.().then();
    }
    createFormQuestionModalProps.onModalClose();
  };

  const _filteringModel = filteringModel([
    { columnField: 'searchValue', value: searchProps.value, operatorValue: 'contains' },
  ]);

  return (
    <ProjectContainer title={'Forms'}>
      <Components.Container>
        <Components.HorizontalHeaderWrapper>
          <Components.QuestionTypeWrapper>
            <Components.FieldLabel>Question Type</Components.FieldLabel>
            <ProjectPicker dropdownProps={formQuestionTypePicker} notSetText={'Select an option'} />
            <ProjectSearchDropdown {...formQuestionTypePicker} />
          </Components.QuestionTypeWrapper>

          {!!formQuestionType && formQuestionType?.length > 0 && (
            <ProjectTableHeader
              onSearchChange={searchProps.onChangeText}
              searchLabel={`Search by question text or ID`}
              searchPlaceholder={'Enter search term'}>
              <Components.SearchBarChildrenWrapper>
                <Components.SearchBarSeparator />

                <Components.PrimaryButton onClick={onAddFormQuestion}>
                  {'Add ' +
                    (formQuestionType === 'reflection'
                      ? getDisplayNames.formQuestionType(formQuestionType)
                      : `${getDisplayNames.formQuestionType(formQuestionType)} Question`)}
                </Components.PrimaryButton>
              </Components.SearchBarChildrenWrapper>
            </ProjectTableHeader>
          )}
        </Components.HorizontalHeaderWrapper>

        {!!formQuestionType && formQuestionType?.length > 0 && (
          <Components.TableWrapper>
            {formQuestionType === ('reflection' as FormQuestionType) ? (
              <ProjectTable
                data={data || []}
                columns={tableColumns.reflectionQuestions({ onEdit: onEditFormQuestion })}
                filteringModel={_filteringModel}
                showRowCountText={'Found reflections'}
              />
            ) : formQuestionType === ('journal' as FormQuestionType) ? (
              <ProjectTable
                data={data || []}
                columns={tableColumns.journalQuestions({ onEdit: onEditFormQuestion })}
                filteringModel={_filteringModel}
              />
            ) : formQuestionType === ('pillarSurvey' as FormQuestionType) ? (
              <ProjectTable
                data={data || []}
                columns={tableColumns.pillarSurveyQuestions({ onEdit: onEditFormQuestion })}
                filteringModel={_filteringModel}
                showRowCountText={'Found pillar survey questions'}
              />
            ) : null}
          </Components.TableWrapper>
        )}
        <CreateOrEditFormQuestionModal
          {...createFormQuestionModalProps}
          onModalClose={onCreateOrEditModalClose}
          existingQuestionKeys={data.map((item) => item.key || '').filter((item) => item !== '')}
        />
      </Components.Container>
    </ProjectContainer>
  );
};

export default Forms;
